// src/pages/Login.js - Enhanced Security for Board Members Only
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/Login.css';
import logo from '../assets/logo_polderzicht_kantoorlaag.png';
import api from '../components/api';
import { isPathAllowed, firstAllowedPath } from '../config/pages';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimeRemaining, setLockTimeRemaining] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || '/beheer';
  
  // Refs for cleanup
  const lockTimerRef = useRef(null);
  const componentMountedRef = useRef(true);

  // Security constants
  const MAX_FAILED_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
      if (lockTimerRef.current) {
        clearInterval(lockTimerRef.current);
      }
    };
  }, []);

  // Check for existing lockout on component mount
  useEffect(() => {
    const checkLockout = () => {
      const lockoutData = localStorage.getItem('board_login_lockout');
      if (lockoutData) {
        try {
          const { timestamp, attempts, email: lockedEmail } = JSON.parse(lockoutData);
          const timePassed = Date.now() - timestamp;
          
          if (timePassed < LOCKOUT_DURATION) {
            setIsLocked(true);
            setFailedAttempts(attempts);
            setLockTimeRemaining(Math.ceil((LOCKOUT_DURATION - timePassed) / 1000));
            setError(`Account tijdelijk vergrendeld. E-mail: ${lockedEmail}`);
            startLockoutTimer();
          } else {
            // Lockout expired, clear it
            localStorage.removeItem('board_login_lockout');
          }
        } catch (e) {
          // Invalid lockout data, remove it
          localStorage.removeItem('board_login_lockout');
        }
      }
    };

    checkLockout();
  }, []);

  // Start lockout countdown timer
  const startLockoutTimer = () => {
    if (lockTimerRef.current) {
      clearInterval(lockTimerRef.current);
    }

    lockTimerRef.current = setInterval(() => {
      if (!componentMountedRef.current) return;

      setLockTimeRemaining(prev => {
        if (prev <= 1) {
          setIsLocked(false);
          setFailedAttempts(0);
          setError('');
          localStorage.removeItem('board_login_lockout');
          clearInterval(lockTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Enhanced input sanitization
  const sanitizeInput = (input) => {
    return input.trim().replace(/[<>'"]/g, '');
  };

  // Enhanced email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  // Password strength validation - Board member requirements
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Wachtwoord moet minimaal 8 karakters bevatten';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Wachtwoord moet minimaal één hoofdletter bevatten';
    }
    if (!/[a-z]/.test(password)) {
      return 'Wachtwoord moet minimaal één kleine letter bevatten';
    }
    if (!/[0-9]/.test(password)) {
      return 'Wachtwoord moet minimaal één cijfer bevatten';
    }
    return null;
  };

  // Handle failed login attempt
  const handleFailedAttempt = (attemptedEmail) => {
    const newFailedAttempts = failedAttempts + 1;
    setFailedAttempts(newFailedAttempts);

    if (newFailedAttempts >= MAX_FAILED_ATTEMPTS) {
      const lockoutData = {
        timestamp: Date.now(),
        attempts: newFailedAttempts,
        email: attemptedEmail
      };
      localStorage.setItem('board_login_lockout', JSON.stringify(lockoutData));
      setIsLocked(true);
      setLockTimeRemaining(LOCKOUT_DURATION / 1000);
      startLockoutTimer();
      setError(`Account vergrendeld voor 15 minuten vanwege te veel mislukte pogingen. Een beveiligingsmelding is verstuurd.`);
    } else {
      const remainingAttempts = MAX_FAILED_ATTEMPTS - newFailedAttempts;
      setError(`Ongeldige inloggegevens. Nog ${remainingAttempts} poging${remainingAttempts !== 1 ? 'en' : ''} over voordat het account wordt vergrendeld.`);
    }
  };

  // Clear failed attempts on successful login
  const clearFailedAttempts = () => {
    setFailedAttempts(0);
    localStorage.removeItem('board_login_lockout');
    if (lockTimerRef.current) {
      clearInterval(lockTimerRef.current);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Check if locked out
    if (isLocked) {
      setError(`Account vergrendeld. Probeer over ${Math.ceil(lockTimeRemaining / 60)} minuten opnieuw.`);
      return;
    }

    // Clear previous errors
    setError('');
    setIsLoading(true);

    // Client-side validation
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = password; // Don't sanitize password as it might contain special chars

    // Validate email
    if (!isValidEmail(sanitizedEmail)) {
      setError('Voer een geldig e-mailadres in');
      setIsLoading(false);
      return;
    }

    // Validate password strength (for new passwords)
    const passwordError = validatePassword(sanitizedPassword);
    if (passwordError && failedAttempts === 0) {
      // Only show password requirements on first attempt to avoid confusion
      console.log('Password validation:', passwordError);
    }

    try {
      // Add security information to request
      const response = await api.post('/api/auth/secure-login', { 
        email: sanitizedEmail, 
        password: sanitizedPassword,
        timestamp: Date.now(),
        userAgent: navigator.userAgent.substring(0, 200),
        failedAttempts: failedAttempts
      });
      
      if (response.data.accessToken) {
        // Clear any failed attempts
        clearFailedAttempts();
        
        // Store tokens securely
        const tokenExpiry = Date.now() + (55 * 60 * 1000); // 55 minutes
        localStorage.setItem('token', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken || '');
        localStorage.setItem('tokenExpiry', tokenExpiry.toString());
        localStorage.setItem('loginTimestamp', Date.now().toString());

        // Rol + pagina-rechten bewaren voor sidebar en route-guard
        localStorage.setItem('userRole', response.data.user?.role || 'board_member');
        localStorage.setItem('allowedPages', JSON.stringify(response.data.user?.allowedPages || []));

        // Log successful login (for security monitoring)
        console.log('Successful login at', new Date().toISOString());

        // Bestemming volledig op basis van de pagina-rechten (user management),
        // voor iedereen gelijk — ook de huismeester. Honoreer de oorspronkelijke
        // deep-link als die mag, anders naar de eerste toegestane pagina.
        const allowed = response.data.user?.allowedPages || [];
        const dest = isPathAllowed(fromPath, allowed) ? fromPath : firstAllowedPath(allowed);
        navigate(dest, { replace: true });
      } else {
        handleFailedAttempt(sanitizedEmail);
      }
    } catch (err) {
      console.error('Login failed:', err);
      
      // Handle different types of errors
      if (err.response?.status === 429) {
        setError('Te veel loginpogingen. Probeer het over 15 minuten opnieuw.');
      } else if (err.response?.status === 401) {
        handleFailedAttempt(sanitizedEmail);
      } else if (err.response?.status === 423) {
        setError('Account tijdelijk vergrendeld. Een beveiligingsmelding is verstuurd naar het bestuur.');
        setIsLocked(true);
      } else {
        handleFailedAttempt(sanitizedEmail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Format lockout time display
  const formatLockoutTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="pz-login-container">
      <div className="pz-login-form-wrapper">
        <img src={logo} alt="Polderzicht Logo" className="pz-login-logo" />
        <h2 className="pz-login-heading">Inloggen</h2>
        
        {error && <div className="pz-login-error">{error}</div>}
        
        {isLocked && (
          <div className="pz-login-lockout">
            <strong>🔒 Account Vergrendeld</strong>
            <br />
            Tijd over: {formatLockoutTime(lockTimeRemaining)}
            <br />
            <small>Een beveiligingsmelding is verstuurd.</small>
          </div>
        )}
        
        <form onSubmit={handleLogin} className="pz-login-form-inner">
          <div className="pz-login-input-group">
            <label className="pz-login-label" htmlFor="pz-login-email">E-mailadres:</label>
            <input
              id="pz-login-email"
              className="pz-login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading || isLocked}
              autoComplete="email"
              maxLength={254}
              spellCheck="false"
              autoCapitalize="none"
              placeholder="bestuur@example.com"
            />
          </div>
          
          <div className="pz-login-input-group">
            <label className="pz-login-label" htmlFor="pz-login-password">Wachtwoord:</label>
            <input
              id="pz-login-password"
              className="pz-login-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading || isLocked}
              autoComplete="current-password"
              maxLength={128}
              minLength={8}
              placeholder="Minimaal 8 tekens, hoofdletter, kleine letter, cijfer"
            />
          </div>
          
          <button 
            type="submit" 
            className={`pz-login-button ${isLoading ? 'pz-login-button-loading' : ''}`}
            disabled={!email || !password || isLoading || isLocked}
          >
            {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
          </button>
        </form>
        
        {failedAttempts > 0 && !isLocked && (
          <div className="pz-login-warning">
            ⚠️ {failedAttempts} van {MAX_FAILED_ATTEMPTS} pogingen gebruikt
            {failedAttempts >= 3 && (
              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                Bij 5 pogingen wordt het account 15 minuten vergrendeld
              </div>
            )}
          </div>
        )}
        
        <div className="pz-login-footer">
          <p><strong>Alleen voor bestuur en huismeester</strong></p>
          <p>Problemen met inloggen? Neem contact op via <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
          <p style={{ fontSize: '11px', color: '#999', marginTop: '15px' }}>
            Wachtwoord vereisten: minimaal 8 tekens, hoofdletter, kleine letter en cijfer
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;